import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconTrash } from '../components/icons';
import { useConfig } from '../context/ConfigContext';

interface LineForm {
  chart_of_account_id: number | '';
  debit_amount: string;
  credit_amount: string;
  description: string;
}

const emptyLine: LineForm = { chart_of_account_id: '', debit_amount: '', credit_amount: '', description: '' };

interface JournalEntryLoaded {
  entry_date: string;
  description: string;
  entry_type: string;
  status: string;
  lines: { chart_of_account_id: number; debit_amount: string; credit_amount: string; description: string | null }[];
}

export function JournalEntryCreate() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { settings } = useConfig();

  const [entryType, setEntryType] = useState<'general' | 'contra'>(
    !id && searchParams.get('contra') === '1' ? 'contra' : 'general'
  );
  const isContra = entryType === 'contra';

  const [chartOfAccounts, setChartOfAccounts] = useState<{ id: number; code: string; name: string }[]>([]);
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<LineForm[]>(
    isContra && settings.gl_cash_bank_chart_of_account_id
      ? [{ ...emptyLine, chart_of_account_id: settings.gl_cash_bank_chart_of_account_id }, { ...emptyLine }]
      : [{ ...emptyLine }, { ...emptyLine }]
  );
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [loaded, setLoaded] = useState(!id);

  useEffect(() => {
    api.get<{ id: number; code: string; name: string }[]>('/chart-of-accounts').then((res) => setChartOfAccounts(res.data));
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get<JournalEntryLoaded>(`/journal-entries/${id}`).then((res) => {
      setEntryDate(res.data.entry_date);
      setDescription(res.data.description);
      setEntryType(res.data.entry_type === 'contra' ? 'contra' : 'general');
      setLines(
        res.data.lines.map((l) => ({
          chart_of_account_id: l.chart_of_account_id,
          debit_amount: Number(l.debit_amount) > 0 ? l.debit_amount : '',
          credit_amount: Number(l.credit_amount) > 0 ? l.credit_amount : '',
          description: l.description ?? '',
        }))
      );
      setLoaded(true);
    });
  }, [id]);

  const totalDebit = lines.reduce((sum, l) => sum + (Number(l.debit_amount) || 0), 0);
  const totalCredit = lines.reduce((sum, l) => sum + (Number(l.credit_amount) || 0), 0);
  const isBalanced = lines.length >= 2 && totalDebit > 0 && Math.abs(totalDebit - totalCredit) < 0.005;

  const updateLine = (index: number, patch: Partial<LineForm>) => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };

  const addLine = () => setLines((prev) => [...prev, { ...emptyLine }]);
  const removeLine = (index: number) => setLines((prev) => prev.filter((_, i) => i !== index));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = {
      entry_date: entryDate,
      description,
      entry_type: entryType,
      lines: lines
        .filter((l) => l.chart_of_account_id)
        .map((l) => ({
          chart_of_account_id: l.chart_of_account_id,
          debit_amount: l.debit_amount || 0,
          credit_amount: l.credit_amount || 0,
          description: l.description || null,
        })),
    };
    try {
      if (id) {
        await api.put(`/journal-entries/${id}`, payload);
        navigate(`/gl/journal-entries/${id}`);
      } else {
        const res = await api.post<{ id: number }>('/journal-entries', payload);
        navigate(`/gl/journal-entries/${res.data.id}`);
      }
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] });
    }
  };

  if (!loaded) return null;

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back" onClick={() => navigate(-1)} />
          <h1>{id ? 'Edit journal entry' : isContra ? 'New contra entry' : 'New correcting journal entry'}</h1>
        </div>
      </header>

      {isContra && (
        <p className="muted">
          A transfer between two of the Fund’s own accounts — for example moving cash between the main bank account
          and petty cash. Pick the two accounts and the amount below; it posts through the same draft-then-post
          control as any other journal entry.
        </p>
      )}

      <form onSubmit={submit} className="card">
        {errors.general && <div className="alert alert-error">{errors.general[0]}</div>}
        <div className="form-grid">
          <label>
            Entry date *
            <input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} required />
            {errors.entry_date && <small className="error">{errors.entry_date[0]}</small>}
          </label>
          <label>
            Type
            <select value={entryType} onChange={(e) => setEntryType(e.target.value as 'general' | 'contra')}>
              <option value="general">General journal entry</option>
              <option value="contra">Contra entry (Cash & Bank transfer)</option>
            </select>
            {errors.entry_type && <small className="error">{errors.entry_type[0]}</small>}
          </label>
          <label className="span-2">
            Description *
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              maxLength={1000}
              placeholder={isContra ? 'e.g. Transfer from main account to petty cash' : undefined}
            />
            {errors.description && <small className="error">{errors.description[0]}</small>}
          </label>
        </div>

        <h2>Lines</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Account</th>
                <th>Description</th>
                <th>Debit</th>
                <th>Credit</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, i) => (
                <tr key={i}>
                  <td>
                    <select
                      value={line.chart_of_account_id}
                      onChange={(e) => updateLine(i, { chart_of_account_id: e.target.value ? Number(e.target.value) : '' })}
                      required
                    >
                      <option value="">Select…</option>
                      {chartOfAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} — {a.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input value={line.description} onChange={(e) => updateLine(i, { description: e.target.value })} />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.debit_amount}
                      onChange={(e) => updateLine(i, { debit_amount: e.target.value, credit_amount: e.target.value ? '' : line.credit_amount })}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.credit_amount}
                      onChange={(e) => updateLine(i, { credit_amount: e.target.value, debit_amount: e.target.value ? '' : line.debit_amount })}
                    />
                  </td>
                  <td>
                    <IconButton
                      icon={IconTrash}
                      label="Remove line"
                      variant="danger-ghost"
                      onClick={() => removeLine(i)}
                      disabled={lines.length <= 2}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2} className="right">
                  <strong>Totals</strong>
                </td>
                <td>
                  <strong>{totalDebit.toFixed(2)}</strong>
                </td>
                <td>
                  <strong>{totalCredit.toFixed(2)}</strong>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
        {errors.lines && <small className="error">{errors.lines[0]}</small>}

        <div className="toolbar">
          <button type="button" className="btn btn-ghost" onClick={addLine}>
            + Add Line
          </button>
          <span className={isBalanced ? 'badge badge-active' : 'badge badge-scheduled'}>
            {isBalanced
              ? `Balanced — ${settings.currency} ${totalDebit.toFixed(2)}`
              : `Not balanced yet (Dr ${totalDebit.toFixed(2)} / Cr ${totalCredit.toFixed(2)})`}
          </span>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button className="btn btn-primary">Save Draft</button>
        </div>
      </form>
    </>
  );
}
